/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { AuthContext } from '../Auth/AuthContext';
import { usePlanPermissions } from '../PlanPermissionsContext';
import { socketManager } from '../Socket/SocketContext';
import { getAiCredits } from '../../services/aiCreditsService';
import { pickTenantCredits } from '../../components/AiCredits/utils';
import { canManageSubscription } from '../../utils/userPaths';

/**
 * Fonte única do saldo de IA da empresa para os indicadores in-app
 * (donut na top bar + banner de alerta). Faz UM fetch de /ai/credits e mantém
 * UMA assinatura de socket, em vez de cada componente buscar por conta própria.
 *
 * Gate único (memória/cache, sem rede): quem gerencia assinatura
 * (dono OU super — `canManageSubscription` já inclui o super) e cujo plano tem
 * crédito de plataforma. Só depois disso a request /ai/credits dispara.
 */
const AiCreditsContext = createContext({
  enabled: false,
  credits: null,
  reload: () => {},
});

// Eventos que mudam o período/saldo de forma estrutural (renovação, recarga) —
// valem um refetch para trazer incluído/consumido corretos. Raros.
const RELOAD_ACTIONS = ['renewed', 'credits_released', 'credits_added', 'request_approved'];

export function AiCreditsProvider({ children }) {
  const { user } = useContext(AuthContext);
  const { allowAiPlatformCredits } = usePlanPermissions();

  const enabled = canManageSubscription(user) && Boolean(allowAiPlatformCredits);
  const companyId = user?.companyId;

  const [credits, setCredits] = useState(null); // shape de pickTenantCredits

  const load = useCallback(async () => {
    try {
      const { data } = await getAiCredits();
      setCredits(pickTenantCredits(data));
    } catch (_) {
      // Silencioso: indicador auxiliar não pode virar toast de erro.
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setCredits(null);
      return undefined;
    }
    load();
    return undefined;
  }, [enabled, load]);

  useEffect(() => {
    if (!enabled || !companyId) return undefined;
    let socket;
    try {
      socket = socketManager.GetSocket();
    } catch (_) {
      return undefined;
    }
    const channel = `company-${companyId}-ai-credits`;
    const handler = (payload) => {
      if (!payload) return;
      const { action } = payload;
      if (RELOAD_ACTIONS.includes(action)) {
        load();
        return;
      }
      // Débito/alerta ({ action, planBalance, extraBalance, total }): atualiza do
      // payload sem refetch. `planIncluded` é estático do plano — preserva o do
      // fetch inicial (o socket não o envia), senão o denominador da % zeraria.
      setCredits((prev) => ({
        ...(prev || {}),
        status: action,
        planBalance: Number(payload.planBalance) || 0,
        extraBalance: Number(payload.extraBalance) || 0,
        totalBalance: Number(payload.total) || 0,
        planIncluded: prev?.planIncluded ?? 0,
      }));
    };
    socket.on(channel, handler);
    return () => socket.off(channel, handler);
  }, [enabled, companyId, load]);

  return (
    <AiCreditsContext.Provider value={{ enabled, credits, reload: load }}>
      {children}
    </AiCreditsContext.Provider>
  );
}

export function useAiCredits() {
  return useContext(AiCreditsContext);
}
